import React from "react";

import ExternalLink from "../../external-link";
import { useSiteMetadata } from "../../../hooks/use-site-metadata";

export default function HcpIsi() {
    const { externalLinkInfo } = useSiteMetadata();
    const { dailymedLinkInfo } = useSiteMetadata();

    return (
        <section className="content-container">
            <div className="centered-content">
                <div className="isi-content">
                    <p className="isi-h1 first">IMPORTANT SAFETY INFORMATION</p>
                    <div className="boxed-warning">
                        <p className="strong">
                            WARNING: SERIOUS AND LIFE-THREATENING RISKS FROM USE
                            OF HYSINGLA ER
                        </p>
                        <p className="strong">Addiction, Abuse, and Misuse</p>
                        <p>
                            Because the use of HYSINGLA ER exposes patients and
                            other users to the risks of opioid addiction, abuse,
                            and misuse, which can lead to overdose and death,
                            assess each patient's risk prior to prescribing and
                            reassess all patients regularly for the development
                            of these behaviors and conditions.
                        </p>

                        <p className="strong">
                            Life-Threatening Respiratory Depression
                        </p>
                        <p>
                            Serious, life-threatening, or fatal respiratory
                            depression may occur with use of HYSINGLA ER,
                            especially during initiation or following a dosage.
                            To reduce the risk of respiratory depression, proper
                            dosing and titration of HYSINGLA ER are essential.
                            Instruct patients to swallow HYSINGLA ER tablets
                            whole; crushing, chewing, or dissolving HYSINGLA ER
                            tablets can cause rapid release and absorption of a
                            potentially fatal dose of hydrocodone.
                        </p>
                        <p className="strong">Accidental Ingestion</p>
                        <p>
                            Accidental ingestion of even one dose of HYSINGLA
                            ER, especially by children, can result in a fatal
                            overdose of hydrocodone.
                        </p>

                        <p className="strong">
                            Risks From Concomitant Use With Benzodiazepines or
                            Other CNS Depressants
                        </p>
                        <p>
                            Concomitant use of opioids with benzodiazepines or
                            other central nervous system (CNS) depressants,
                            including alcohol, may result in profound sedation,
                            respiratory depression, coma, and death. Reserve
                            concomitant prescribing of HYSINGLA ER and
                            benzodiazepines or other CNS depressants for use in
                            patients for whom alternative treatment options are
                            inadequate.
                        </p>

                        <p className="strong">
                            Neonatal Opioid Withdrawal Syndrome (NOWS)
                        </p>
                        <p>
                            If opioid use is required for an extended period of
                            time in a pregnant woman, advise the patient of the
                            risk of NOWS, which may be life-threatening if not
                            recognized and treated. Ensure that management by
                            neonatology experts will be available at delivery.
                        </p>

                        <p className="strong">
                            Opioid Analgesic Risk Evaluation and Mitigation
                            Strategy (REMS)
                        </p>
                        <p>
                            Healthcare providers are strongly encouraged to
                            complete a REMS-compliant education program and to
                            counsel patients and caregivers on serious risks,
                            safe use, and the importance of reading the
                            Medication Guide with each prescription.
                        </p>

                        <p className="strong">
                            Cytochrome P450 3A4 Interaction
                        </p>
                        <p>
                            The concomitant use of HYSINGLA ER with all
                            cytochrome P450 3A4 inhibitors may result in an
                            increase in hydrocodone plasma concentrations, which
                            could increase or prolong adverse reactions and may
                            cause potentially fatal respiratory depression. In
                            addition, discontinuation of a concomitantly used
                            cytochrome P450 3A4 inducer may result in an
                            increase in hydrocodone plasma concentration.
                            Regularly evaluate patients receiving HYSINGLA ER
                            and any CYP3A4 inhibitor or inducer.
                        </p>
                    </div>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">CONTRAINDICATIONS</p>
                    <p>HYSINGLA ER is contraindicated in patients with:</p>
                    <ul className="bullet-list">
                        <li>Significant respiratory depression</li>
                        <li>
                            Acute or severe bronchial asthma in an unmonitored
                            setting or in the absence of resuscitative equipment
                        </li>
                        <li>
                            Known or suspected gastrointestinal obstruction,
                            including paralytic ileus
                        </li>
                        <li>
                            Hypersensitivity to hydrocodone or any component of
                            HYSINGLA ER
                        </li>
                    </ul>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">WARNINGS AND PRECAUTIONS</p>
                    <p className="strong">Addiction, Abuse, and Misuse</p>
                    <p>
                        HYSINGLA ER contains hydrocodone, a Schedule II
                        controlled substance. As an opioid, HYSINGLA ER exposes
                        users to the risks of addiction, abuse, and misuse.
                        Because extended-release products such as HYSINGLA ER
                        deliver the opioid over an extended period of time,
                        there is a greater risk for overdose and death due to
                        the larger amount of hydrocodone present.
                    </p>
                    <p>
                        Although the risk of addiction in any individual is
                        unknown, it can occur in patients appropriately
                        prescribed HYSINGLA ER. Addiction can occur at
                        recommended doses and if the drug is misused or abused.
                    </p>
                    <p>
                        Assess each patient's risk for opioid addiction, abuse,
                        or misuse prior to prescribing HYSINGLA ER, and reassess
                        all patients receiving HYSINGLA ER for the development
                        of these behaviors and conditions. Risks are increased
                        in patients with a personal or family history of
                        substance abuse (including drug or alcohol abuse or
                        addiction) or mental illness (e.g., major depression).
                        The potential for these risks should not, however,
                        prevent the prescribing of HYSINGLA ER for the proper
                        management of pain in any given patient. Patients at
                        increased risk may be prescribed opioids such as
                        HYSINGLA ER, but use in such patients necessitates
                        intensive counseling about the risks and proper use of
                        HYSINGLA ER along with frequent reevaluation for signs
                        of addiction, abuse, and misuse. Consider prescribing
                        naloxone for the emergency treatment of opioid overdose.
                    </p>
                    <p>
                        Abuse or misuse of HYSINGLA ER by crushing, chewing,
                        snorting, or injecting the dissolved product will result
                        in the uncontrolled delivery of the hydrocodone and can
                        result in overdose and death.
                    </p>
                    <p>
                        Opioids are sought for nonmedical use and are subject to
                        diversion from legitimate prescribed use. Consider these
                        risks when prescribing or dispensing HYSINGLA ER.
                        Strategies to reduce these risks include prescribing the
                        drug in the smallest appropriate quantity and advising
                        the patient on careful storage of the drug during the
                        course of treatment and the proper disposal of unused
                        drug. Contact local state professional licensing board
                        or state-controlled substances authority for information
                        on how to prevent and detect abuse or diversion of this
                        product.
                    </p>

                    <p className="strong">
                        Life-Threatening Respiratory Depression
                    </p>
                    <p>
                        Serious, life-threatening, or fatal respiratory
                        depression has been reported with the use of opioids,
                        even when used as recommended. Respiratory depression,
                        if not immediately recognized and treated, may lead to
                        respiratory arrest and death. Management of respiratory
                        depression may include close observation, supportive
                        measures, and use of opioid antagonists, depending on
                        the patient's clinical status. Carbon dioxide (CO
                        <sub>2</sub>) retention from opioid-induced respiratory
                        depression can exacerbate the sedating effects of
                        opioids.
                    </p>
                    <p>
                        While serious, life-threatening, or fatal respiratory
                        depression can occur at any time during the use of
                        HYSINGLA ER, the risk is greatest during the initiation
                        of therapy or following a dosage increase.
                    </p>
                    <p>
                        To reduce the risk of respiratory depression, proper
                        dosing and titration of HYSINGLA ER are essential.
                        Overestimating the HYSINGLA ER dosage when converting
                        patients from another opioid product can result in fatal
                        overdose with the first dose.
                    </p>
                    <p>
                        Accidental ingestion of even one dose of HYSINGLA ER,
                        especially by children, can result in respiratory
                        depression and death due to an overdose of hydrocodone.
                    </p>
                    <p>
                        Educate patients and caregivers on how to recognize
                        respiratory depression and emphasize the importance of
                        calling 911 or getting emergency medical help right away
                        in the event of a known or suspected overdose.
                    </p>
                    <p>
                        Opioids can cause sleep-related breathing disorders
                        including central sleep apnea (CSA) and sleep-related
                        hypoxemia. Opioid use increases the risk of CSA in a
                        dose-dependent fashion. In patients who present with
                        CSA, consider decreasing the opioid dosage using best
                        practices for opioid taper.
                    </p>

                    <p className="text-underline">
                        Patient Access to Naloxone for the Emergency Treatment
                        of Opioid Overdose:
                    </p>
                    <p>
                        Discuss the availability of naloxone for the emergency
                        treatment of opioid overdose with the patient and
                        caregiver and assess the potential need for access to
                        naloxone, both when initiating and renewing treatment
                        with HYSINGLA ER. Inform patients and caregivers about
                        the various ways to obtain naloxone as permitted by
                        individual state naloxone dispensing and prescribing
                        requirements or guidelines (e.g., by prescription,
                        directly from a pharmacist, or as part of a
                        community-based program). Educate patients and
                        caregivers on how to recognize respiratory depression
                        and emphasize the importance of calling 911 or getting
                        emergency medical help, even if naloxone is
                        administered.
                    </p>
                    <p>
                        Consider prescribing naloxone, based on the patient's
                        risk factors for overdose, such as concomitant use of
                        CNS depressants, a history of opioid use disorder, or
                        prior opioid overdose. The presence of risk factors for
                        overdose should not prevent the proper management of
                        pain in any given patient. Also consider prescribing
                        naloxone if the patient has household members (including
                        children) or other close contacts at risk for accidental
                        ingestion or overdose. If naloxone is prescribed,
                        educate patients and caregivers on how to treat with
                        naloxone.
                    </p>

                    <p className="strong">
                        Risks from Concomitant Use with Benzodiazepines or Other
                        CNS Depressants
                    </p>
                    <p>
                        Profound sedation, respiratory depression, coma, and
                        death may result from the concomitant use of HYSINGLA ER
                        with benzodiazepines and/or other CNS depressants,
                        including alcohol (e.g., non-benzodiazepine
                        sedatives/hypnotics, anxiolytics, tranquilizers, muscle
                        relaxants, general anesthetics, antipsychotics, other
                        opioids). Because of these risks, reserve concomitant
                        prescribing of these drugs for use in patients for whom
                        alternative treatment options are inadequate.
                    </p>
                    <p>
                        Observational studies have demonstrated that concomitant
                        use of opioid analgesics and benzodiazepines increases
                        the risk of drug-related mortality compared to use of
                        opioid analgesics alone. Because of similar
                        pharmacological properties, it is reasonable to expect
                        similar risk with the concomitant use of other CNS
                        depressant drugs with opioid analgesics.
                    </p>
                    <p>
                        If the decision is made to prescribe a benzodiazepine or
                        other CNS depressant concomitantly with an opioid
                        analgesic, prescribe the lowest effective dosages and
                        minimum durations of concomitant use. In patients
                        already receiving an opioid analgesic, prescribe a lower
                        initial dose of the benzodiazepine or other CNS
                        depressant than indicated in the absence of an opioid,
                        and titrate based on clinical response. If an opioid
                        analgesic is initiated in a patient already taking a
                        benzodiazepine or other CNS depressant, prescribe a
                        lower initial dose of the opioid analgesic, and titrate
                        based on clinical response. Inform patients and
                        caregivers of this potential interaction and educate
                        them on the signs and symptoms of respiratory depression
                        (including sedation).
                    </p>
                    <p>
                        If concomitant use is warranted, consider prescribing
                        naloxone for the emergency treatment of opioid overdose.
                    </p>
                    <p>
                        Advise both patients and caregivers about the risks of
                        respiratory depression and sedation when HYSINGLA ER is
                        used with benzodiazepines or other CNS depressants
                        (including alcohol and illicit drugs). Advise patients
                        not to drive or operate heavy machinery until the
                        effects of concomitant use of the benzodiazepine or
                        other CNS depressant have been determined. Screen
                        patients for risk of substance use disorders, including
                        opioid abuse and misuse, and warn them of the risk for
                        overdose and death associated with the use of additional
                        CNS depressants including alcohol and illicit drugs.
                    </p>

                    <p className="strong">
                        Neonatal Opioid Withdrawal Syndrome
                    </p>
                    <p>
                        Use of HYSINGLA ER for an extended period of time during
                        pregnancy can result in withdrawal in the neonate.
                        Neonatal opioid withdrawal syndrome, unlike opioid
                        withdrawal syndrome in adults, may be life-threatening
                        if not recognized and treated, and requires management
                        according to protocols developed by neonatology experts.
                        Observe newborns for signs of neonatal opioid withdrawal
                        syndrome and manage accordingly. Advise pregnant women
                        using opioids for an extended period of time of the risk
                        of neonatal opioid withdrawal syndrome and ensure that
                        appropriate treatment will be available
                    </p>

                    <p className="strong">
                        Opioid Analgesic Risk Evaluation and Mitigation Strategy
                        (REMS)
                    </p>
                    <p>
                        To ensure that the benefits of opioid analgesics
                        outweigh the risks of addiction, abuse, and misuse, the
                        Food and Drug Administration (FDA) has required a Risk
                        Evaluation and Mitigation Strategy (REMS) for these
                        products. Under the requirements of the REMS, drug
                        companies with approved opioid analgesic products must
                        make REMS-compliant education programs available to
                        healthcare providers. Healthcare providers are strongly
                        encouraged to do all of the following:
                    </p>
                    <ul className="bullet-list">
                        <li>
                            Complete a REMS-compliant education program offered
                            by an accredited provider of continuing education
                            (CE) or another education program that includes all
                            the elements of the FDA Education Blueprint for
                            Health Care Providers Involved in the Management or
                            Support of Patients with Pain.
                        </li>
                        <li>
                            Discuss the safe use, serious risks, and proper
                            storage and disposal of opioid analgesics with
                            patients and/or their caregivers every time these
                            medicines are prescribed. The Patient Counseling
                            Guide (PCG) can be obtained at this link:{" "}
                            <ExternalLink
                                externalURL="https://www.fda.gov/OpioidAnalgesicREMSPCG"
                                messageTitle={externalLinkInfo.TITLE}
                                messageBody={externalLinkInfo.BODY}
                                classNameLink={externalLinkInfo.LINK_CLASS_NAME}
                                classNameOverlay={
                                    externalLinkInfo.OVERLAY_CLASS_NAME
                                }
                            >
                                www.fda.gov/OpioidAnalgesicREMSPCG
                            </ExternalLink>
                            .
                        </li>
                        <li>
                            Emphasize to patients and their caregivers the
                            importance of reading the Medication Guide that they
                            will receive from their pharmacist every time an
                            opioid analgesic is dispensed to them.
                        </li>
                        <li className="last">
                            Consider using other tools to improve patient,
                            household, and community safety, such as
                            patient-prescriber agreements that reinforce
                            patient-prescriber responsibilities.
                        </li>
                    </ul>
                    <p>
                        To obtain further information on the opioid analgesic
                        REMS and for a list of accredited REMS CME/CE, call
                        1-800-503-0784, or log on to{" "}
                        <span>
                            <ExternalLink
                                externalURL="http://www.opioidanalgesicrems.com"
                                messageTitle={externalLinkInfo.TITLE}
                                messageBody={externalLinkInfo.BODY}
                                classNameLink={externalLinkInfo.LINK_CLASS_NAME}
                                classNameOverlay={
                                    externalLinkInfo.OVERLAY_CLASS_NAME
                                }
                            >
                                www.opioidanalgesicrems.com
                            </ExternalLink>
                            . The FDA Blueprint can be found at{" "}
                        </span>
                        <ExternalLink
                            externalURL="http://www.fda.gov/OpioidAnalgesicREMSBlueprint"
                            messageTitle={externalLinkInfo.TITLE}
                            messageBody={externalLinkInfo.BODY}
                            classNameLink={externalLinkInfo.LINK_CLASS_NAME}
                            classNameOverlay={
                                externalLinkInfo.OVERLAY_CLASS_NAME
                            }
                        >
                            www.fda.gov/OpioidAnalgesicREMSBlueprint
                        </ExternalLink>
                    </p>

                    <p className="strong">
                        Risks of Concomitant Use or Discontinuation of
                        Cytochrome P450 3A4 Inhibitors and Inducers
                    </p>
                    <p>
                        Concomitant use of HYSINGLA ER with a CYP3A4 inhibitor,
                        such as macrolide antibiotics (e.g., erythromycin),
                        azole-antifungal agents (e.g., ketoconazole), and
                        protease inhibitors (e.g., ritonavir), may increase
                        plasma concentrations of hydrocodone and prolong opioid
                        adverse reactions, which may cause potentially fatal
                        respiratory depression, particularly when an inhibitor
                        is added after a stable dose of HYSINGLA ER is achieved.
                        Similarly, discontinuation of a CYP3A4 inducer, such as
                        rifampin, carbamazepine, and phenytoin, in HYSINGLA ER
                        treated patients may increase hydrocodone plasma
                        concentrations and prolong opioid adverse reactions.
                        When using HYSINGLA ER with CYP3A4 inhibitors or
                        discontinuing CYP3A4 inducers in HYSINGLA ER-treated
                        patients, evaluate patients at frequent intervals and
                        consider dosage reduction of HYSINGLA ER until stable
                        drug effects are achieved.
                    </p>
                    <p>
                        Concomitant use of HYSINGLA ER with CYP3A4 inducers or
                        discontinuation of an CYP3A4 inhibitor could decrease
                        hydrocodone plasma concentrations, decrease opioid
                        efficacy or, possibly, lead to a withdrawal syndrome in
                        a patient who had developed physical dependence to
                        hydrocodone. When using HYSINGLA ER with CYP3A4 inducers
                        or discontinuing CYP3A4 inhibitors, monitor patients
                        closely at frequent intervals and consider increasing
                        the opioid dosage if needed to maintain adequate
                        analgesia or if symptoms of opioid withdrawal occur.
                    </p>

                    <p className="strong">
                        Opioid-Induced Hyperalgesia and Allodynia
                    </p>
                    <p>
                        Opioid-Induced Hyperalgesia (OIH) occurs when an opioid
                        analgesic paradoxically causes an increase in pain, or
                        an increase in sensitivity to pain. This condition
                        differs from tolerance, which is the need for increasing
                        doses of opioids to maintain a defined effect. Symptoms
                        of OIH include (but may not be limited to) increased
                        levels of pain upon opioid dosage increase, decreased
                        levels of pain upon opioid dosage decrease, or pain from
                        ordinarily non-painful stimuli (allodynia). These
                        symptoms may suggest OIH only if there is no evidence of
                        underlying disease progression, opioid tolerance, opioid
                        withdrawal, or addictive behavior.
                    </p>
                    <p>
                        Cases of OIH have been reported, both with short-term
                        and longer-term use of opioid analgesics. Though the
                        mechanism of OIH is not fully understood, multiple
                        biochemical pathways have been implicated. Medical
                        literature suggests a strong biologic plausibility
                        between opioid analgesics and OIH and allodynia. If a
                        patient is suspected to be experiencing OIH, carefully
                        consider appropriately decreasing the dose of the
                        current opioid analgesic, or opioid rotation (safely
                        switching the patient to a different opioid moiety).
                    </p>

                    <p className="strong">
                        Life-Threatening Respiratory Depression in Patients with
                        Chronic Pulmonary Disease or Elderly, Cachectic, or
                        Debilitated Patients
                    </p>
                    <p>
                        The use of HYSINGLA ER in patients with acute or severe
                        bronchial asthma in an unmonitored setting or in the
                        absence of resuscitative equipment is contraindicated.
                    </p>
                    <p>
                        <span className="text-underline">
                            Patients with Chronic Pulmonary Disease
                        </span>
                        : HYSINGLA ER-treated patients with significant chronic
                        obstructive pulmonary disease or cor pulmonale, and
                        those with a substantially decreased respiratory
                        reserve, hypoxia, hypercapnia, or pre-existing
                        respiratory depression are at increased risk of
                        decreased respiratory drive including apnea, even at
                        recommended dosages of HYSINGLA ER.
                    </p>
                    <p>
                        <span className="text-underline">
                            Elderly, Cachectic, or Debilitated Patients
                        </span>
                        : Life-threatening respiratory depression is more likely
                        to occur in elderly, cachectic, or debilitated patients
                        because they may have altered pharmacokinetics or
                        altered clearance compared to younger, healthier
                        patients.
                    </p>
                    <p>
                        Regularly evaluate patients, particularly when
                        initiating and titrating HYSINGLA ER and when HYSINGLA
                        ER is given concomitantly with other drugs that depress
                        respiration. Alternatively, consider the use of
                        non-opioid analgesics in these patients.
                    </p>

                    <p className="strong">Adrenal Insufficiency</p>
                    <p>
                        Cases of adrenal insufficiency have been reported with
                        opioid use, more often following greater than one month
                        of use. Presentation of adrenal insufficiency may
                        include non-specific symptoms and signs including
                        nausea, vomiting, anorexia, fatigue, weakness,
                        dizziness, and low blood pressure. If adrenal
                        insufficiency is suspected, confirm the diagnosis with
                        diagnostic testing as soon as possible. If adrenal
                        insufficiency is diagnosed, treat with physiologic
                        replacement doses of corticosteroids. Wean the patient
                        off of the opioid to allow adrenal function to recover
                        and continue corticosteroid treatment until adrenal
                        function recovers. Other opioids may be tried as some
                        cases reported use of a different opioid without
                        recurrence of adrenal insufficiency. The information
                        available does not identify any particular opioids as
                        being more likely to be associated with adrenal
                        insufficiency.
                    </p>

                    <p className="strong">Severe Hypotension</p>
                    <p>
                        HYSINGLA ER may cause severe hypotension including
                        orthostatic hypotension and syncope in ambulatory
                        patients. There is an increased risk in patients whose
                        ability to maintain blood pressure has already been
                        compromised by a reduced blood volume, or after
                        concurrent administration of certain CNS depressant
                        drugs (e.g., phenothiazines or general anesthetics).
                        Regularly evaluate these patients for signs of
                        hypotension after initiating or titrating the dosage of
                        HYSINGLA ER. In patients with circulatory shock,
                        HYSINGLA ER may cause vasodilation that can further
                        reduce cardiac output and blood pressure. Avoid the use
                        of HYSINGLA ER in patients with circulatory shock.
                    </p>

                    <p className="strong">QTc Interval Prolongation</p>
                    <p>
                        QTc prolongation has been observed with HYSINGLA ER
                        following daily doses of 160 mg. This observation should
                        be considered in making clinical decisions regarding
                        patient monitoring when prescribing HYSINGLA ER in
                        patients with congestive heart failure,
                        bradyarrhythmias, electrolyte abnormalities, or who are
                        taking medications that are known to prolong the QTc
                        interval.
                    </p>
                    <p>
                        HYSINGLA ER should be avoided in patients with
                        congenital long QT syndrome. In patients who develop QTc
                        prolongation, consider reducing the dose by 33–50%, or
                        changing to an alternate analgesic.
                    </p>

                    <p className="strong">
                        Risk of Use in Patients with Increased Intracranial
                        Pressure, Brain Tumors, Head Injury, or Impaired
                        Consciousness
                    </p>
                    <p>
                        In patients who may be susceptible to the intracranial
                        effects of CO<sub>2</sub> retention (e.g., those with
                        evidence of increased intracranial pressure or brain
                        tumors), HYSINGLA ER may reduce respiratory drive, and
                        the resultant CO<sub>2</sub> retention can further
                        increase intracranial pressure. Monitor such patients
                        for signs of sedation and respiratory depression,
                        particularly when initiating therapy with HYSINGLA ER.
                    </p>
                    <p>
                        Opioids may also obscure the clinical course in a
                        patient with a head injury. Avoid the use of HYSINGLA ER
                        in patients with impaired consciousness or coma.
                    </p>

                    <p className="strong">
                        Gastrointestinal Obstruction, Dysphagia, and Choking
                    </p>
                    <p>
                        In the clinical studies with specific instructions to
                        take HYSINGLA ER with adequate water to swallow the
                        tablet, 11 out of 2476 subjects reported difficulty
                        swallowing HYSINGLA ER. These reports included
                        esophageal obstruction, dysphagia, and choking, one of
                        which had required medical intervention to remove the
                        tablet.
                    </p>
                    <p>
                        Instruct patients not to pre-soak, lick, or otherwise
                        wet HYSINGLA ER tablets prior to placing in the mouth,
                        and to take one tablet at a time with enough water to
                        ensure complete swallowing immediately after placing in
                        the mouth.
                    </p>
                    <p>
                        Patients with underlying gastrointestinal disorders such
                        as esophageal cancer or colon cancer with a small
                        gastrointestinal lumen are at greater risk of developing
                        these complications. Consider use of an alternative
                        analgesic in patients who have difficulty swallowing and
                        patients at risk for underlying gastrointestinal
                        disorders resulting in a small gastrointestinal lumen.
                    </p>
                    <p>
                        Pediatric patients may be at increased risk of
                        esophageal obstruction, dysphagia, and choking because
                        of a smaller gastrointestinal lumen if they ingest
                        HYSINGLA ER.
                    </p>

                    <p className="strong">
                        Risks of Use in Patients with Gastrointestinal
                        Conditions
                    </p>
                    <p>
                        HYSINGLA ER is contraindicated in patients with known or
                        suspected gastrointestinal obstruction, including
                        paralytic ileus.
                    </p>
                    <p>
                        The hydrocodone in HYSINGLA ER may cause spasm of the
                        sphincter of Oddi. Opioids may cause increases in serum
                        amylase. Regularly evaluate patients with biliary tract
                        disease, including acute pancreatitis, for worsening
                        symptoms.
                    </p>

                    <p className="strong">
                        Increased Risk of Seizures in Patients with Seizure
                        Disorders
                    </p>
                    <p>
                        The hydrocodone in HYSINGLA ER may increase the
                        frequency of seizures in patients with seizure
                        disorders, and may increase the risk of seizures
                        occurring in other clinical settings associated with
                        seizures. Regularly evaluate patients with a history of
                        seizure disorders for worsened seizure control during
                        HYSINGLA ER therapy.
                    </p>

                    <p className="strong">Withdrawal</p>
                    <p>
                        Do not abruptly discontinue HYSINGLA ER in a patient
                        physically dependent on opioids. When discontinuing
                        HYSINGLA ER in a physically dependent patient, gradually
                        taper the dosage. Rapid tapering of hydrocodone in a
                        patient physically dependent on opioids may lead to a
                        withdrawal syndrome and return of pain.
                    </p>
                    <p>
                        Additionally, avoid the use of mixed agonist/antagonist
                        analgesics (e.g., pentazocine, nalbuphine, and
                        butorphanol) or partial agonist (e.g., buprenorphine)
                        analgesics in patients who are receiving a full opioid
                        agonist analgesic, including HYSINGLA ER. In these
                        patients, mixed agonist/antagonist and partial agonist
                        analgesics may reduce the analgesic effect and/or may
                        precipitate withdrawal symptoms.
                    </p>

                    <p className="strong">
                        Risks of Driving and Operating Machinery
                    </p>
                    <p>
                        HYSINGLA ER may impair the mental and physical abilities
                        needed to perform potentially hazardous activities such
                        as driving a car or operating machinery. Peak blood
                        levels of hydrocodone may occur 14–16 hours (range 6–30
                        hours) after initial dosing of HYSINGLA ER tablet
                        administration. Blood levels of hydrocodone, in some
                        patients, may be high at the end of 24 hours after
                        repeated-dose administration. Warn patients not to drive
                        or operate dangerous machinery unless they are tolerant
                        to the effects of HYSINGLA ER and know how they will
                        react to the medication.
                    </p>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">ADVERSE REACTIONS</p>
                    <p>
                        The most common adverse reactions (≥5%) reported by
                        patients treated with HYSINGLA ER in the chronic pain
                        clinical trials were constipation, nausea, vomiting,
                        fatigue, upper respiratory tract infection, dizziness,
                        headache, somnolence.
                    </p>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">DRUG INTERACTIONS</p>
                    <p>
                        Clinically significant drug interactions with HYSINGLA
                        ER include:
                    </p>

                    <p>
                        <span className="strong">Inhibitors of CYP3A4:</span>{" "}
                        The concomitant use of HYSINGLA ER and CYP3A4 inhibitors
                        can increase the plasma concentration of hydrocodone,
                        resulting in increased or prolonged opioid effects.
                        These effects could be more pronounced with concomitant
                        use of HYSINGLA ER and CYP3A4 inhibitors, particularly
                        when an inhibitor is added after a stable dose of
                        HYSINGLA ER is achieved.
                    </p>
                    <p>
                        After stopping a CYP3A4 inhibitor, as the effects of the
                        inhibitor decline, the hydrocodone plasma concentration
                        will decrease, resulting in decreased opioid efficacy or
                        a withdrawal syndrome in patients who had developed
                        physical dependence to hydrocodone.
                    </p>

                    <p>
                        <span className="strong">CYP3A4 Inducers:</span> The
                        concomitant use of HYSINGLA ER and CYP3A4 inducers can
                        decrease the plasma concentration of hydrocodone,
                        resulting in decreased efficacy or onset of a withdrawal
                        syndrome in patients who have developed physical
                        dependence to hydrocodone.
                    </p>
                    <p>
                        After stopping a CYP3A4 inducer, as the effects of the
                        inducer decline, the hydrocodone plasma concentration
                        will increase, which could increase or prolong both the
                        therapeutic effects and adverse reactions, and may cause
                        serious respiratory depression.
                    </p>

                    <p>
                        <span className="strong">
                            Benzodiazepines and Other Central Nervous System
                            (CNS) Depressants:
                        </span>{" "}
                        Due to additive pharmacologic effect, the concomitant
                        use of benzodiazepines or other CNS depressants,
                        including alcohol, can increase the risk of hypotension,
                        respiratory depression, profound sedation, coma, and
                        death.
                    </p>

                    <p>
                        <span className="strong">Serotonergic Drugs:</span> The
                        concomitant use of opioids with other drugs that affect
                        the serotonergic neurotransmitter system has resulted in
                        serotonin syndrome.
                    </p>

                    <p>
                        <span className="strong">
                            Monoamine Oxidase Inhibitors (MAOIs):
                        </span>{" "}
                        MAOI interactions with opioids may manifest as serotonin
                        syndrome or opioid toxicity (e.g., respiratory
                        depression, coma).
                    </p>

                    <p>
                        <span className="strong">
                            Mixed Agonist/Antagonist and Partial Agonist Opioid
                            Analgesics:
                        </span>{" "}
                        May reduce the analgesic effect of HYSINGLA ER and/or
                        precipitate withdrawal symptoms.
                    </p>

                    <p>
                        <span className="strong">Muscle Relaxants:</span>{" "}
                        Hydrocodone may enhance the neuromuscular blocking
                        action of skeletal muscle relaxants and produce an
                        increased degree of respiratory depression.
                    </p>

                    <p>
                        <span className="strong">Diuretics:</span> Opioids can
                        reduce the efficacy of diuretics by inducing the release
                        of antidiuretic hormone.
                    </p>

                    <p>
                        <span className="strong">Anticholinergic Drugs:</span>{" "}
                        The concomitant use of anticholinergic drugs may
                        increase risk of urinary retention and/or severe
                        constipation, which may lead to paralytic ileus.
                    </p>

                    <p>
                        <span className="strong">Strong Laxatives:</span>{" "}
                        Concomitant use of HYSINGLA ER with strong laxatives
                        that rapidly increase gastrointestinal motility, may
                        decrease hydrocodone absorption and result in decreased
                        hydrocodone plasma levels.
                    </p>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">USE IN SPECIFIC POPULATIONS</p>
                    <p className="strong">Pregnancy</p>

                    <p className="text-underline">Risk Summary</p>
                    <p>
                        Use of opioid analgesics for an extended period of time
                        during pregnancy may cause neonatal opioid withdrawal
                        syndrome. Available data with HYSINGLA ER in pregnant
                        women are insufficient to inform a drug-associated risk
                        for major birth defects and miscarriage.
                    </p>

                    <p className="text-underline">Clinical Considerations</p>
                    <p className="text-italics">
                        Fetal/neonatal adverse reactions
                    </p>
                    <p>
                        Use of opioid analgesics for extended period of time
                        during pregnancy for medical or nonmedical purposes can
                        result in physical dependence in the neonate and
                        neonatal opioid withdrawal syndrome shortly after birth.
                    </p>

                    <p className="text-italics">Labor and Delivery</p>
                    <p>
                        Opioids cross the placenta and may produce respiratory
                        depression and psycho-physiologic effects in neonates.
                        An opioid antagonist, such as naloxone, must be
                        available for reversal of opioid-induced respiratory
                        depression in the neonate. HYSINGLA ER is not
                        recommended for use in pregnant women during or
                        immediately prior to labor, when use of shorter-acting
                        analgesics or other analgesic techniques are more
                        appropriate. Opioid analgesics, including HYSINGLA ER,
                        can prolong labor through actions which temporarily
                        reduce the strength, duration, and frequency of uterine
                        contractions. However, this effect is not consistent and
                        may be offset by an increased rate of cervical dilation,
                        which tends to shorten labor. Monitor neonates exposed
                        to opioid analgesics during labor for signs of excess
                        sedation and respiratory depression.
                    </p>

                    <p className="strong">Lactation</p>

                    <p className="text-underline">Risk Summary</p>
                    <p>
                        Hydrocodone is present in human milk. A published
                        lactation study reports variable concentrations of
                        hydrocodone and hydromorphone (an active metabolite) in
                        breast milk with administration of immediate-release
                        hydrocodone to nursing mothers in the early post-partum
                        period. This lactation study did not assess breastfed
                        infants for potential adverse drug reactions. Lactation
                        studies have not been conducted with HYSINGLA, and no
                        information is available on the effects of the drug on
                        the breastfed infant or the effects of the drug on milk
                        production. Because of the potential for serious adverse
                        reactions, including excess sedation and respiratory
                        depression in a breastfed infant, advise patients that
                        breastfeeding is not recommended during treatment with
                        HYSINGLA ER.
                    </p>

                    <p className="text-underline">Clinical Considerations</p>
                    <p>
                        Monitor infants exposed to HYSINGLA ER through breast
                        milk for excess sedation and respiratory depression.
                        Withdrawal symptoms can occur in breastfed infants when
                        maternal administration of an opioid analgesic is
                        stopped, or when breastfeeding is stopped.
                    </p>

                    <p className="strong">
                        Females and Males of Reproductive Potential
                    </p>

                    <p className="text-underline">Infertility</p>
                    <p>
                        Use of opioids for an extended period of time may cause
                        reduced fertility in females and males of reproductive
                        potential. It is not known whether these effects on
                        fertility are reversible.
                    </p>

                    <p className="strong">Pediatric Use</p>
                    <p>
                        The safety and effectiveness of HYSINGLA ER in pediatric
                        patients have not been established.
                    </p>
                    <p>
                        HYSINGLA ER gradually forms a viscous hydrogel (i.e., a
                        gelatinous mass) when exposed to water or other fluids.
                        Pediatric patients may be at increased risk of
                        esophageal obstruction, dysphagia, and choking because
                        of a smaller gastrointestinal lumen if they ingest
                        HYSINGLA ER.
                    </p>

                    <p className="strong">Geriatric Use</p>
                    <p>
                        In a controlled pharmacokinetic study, elderly subjects
                        (greater than 65 years) compared to young adults had
                        similar plasma concentrations of hydrocodone [see
                        Clinical Pharmacology (12.3)]. Of the 1827 subjects
                        exposed to HYSINGLA ER in the pooled chronic pain
                        studies, 241 (13%) were age 65 and older (including
                        those age 75 and older), while 42 (2%) were age 75 and
                        older. In clinical trials with appropriate initiation of
                        therapy and dose titration, no untoward or unexpected
                        adverse reactions were seen in the elderly patients who
                        received HYSINGLA ER.
                    </p>
                    <p>
                        In general, use caution when selecting a dosage for an
                        elderly patient, usually starting at the low end of the
                        dosing range, reflecting the greater frequency of
                        decreased hepatic, renal, or cardiac function and of
                        concomitant disease or other drug therapy.
                    </p>
                    <p>
                        Respiratory depression is the chief risk for elderly
                        patients treated with opioids, and has occurred after
                        large initial doses were administered to patients who
                        were not opioid-tolerant or when opioids were
                        co-administered with other agents that depress
                        respiration. Titrate the dosage of HYSINGLA ER slowly in
                        geriatric patients and frequently reevaluate the patient
                        for signs of central nervous system and respiratory
                        depression.
                    </p>
                    <p>
                        Hydrocodone is known to be substantially excreted by the
                        kidney, and the risk of adverse reactions to this drug
                        may be greater in patients with impaired renal function.
                        Because elderly patients are more likely to have
                        decreased renal function, care should be taken in dose
                        selection, and it may be useful to regularly evaluate
                        renal function.
                    </p>

                    <p className="strong">Hepatic Impairment</p>
                    <p>
                        No adjustment in starting dose with HYSINGLA ER is
                        required in patients with mild or moderate hepatic
                        impairment. Patients with severe hepatic impairment may
                        have higher plasma concentrations than those with normal
                        hepatic function. Therefore, a dosage reduction is
                        recommended for these patients. Regularly evaluate
                        closely for respiratory depression, sedation, and
                        hypotension.
                    </p>

                    <p className="strong">Renal Impairment</p>
                    <p>
                        No dose adjustment is needed in patients with mild renal
                        impairment. Patients with moderate or severe renal
                        impairment or end stage renal disease have higher plasma
                        concentrations than those with normal renal function.
                        Therefore, a dosage reduction is recommended for these
                        patients. Regularly evaluate closely for respiratory
                        depression, sedation, and hypotension.
                    </p>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">DRUG ABUSE AND DEPENDENCE</p>
                    <p>
                        HYSINGLA ER contains hydrocodone, a substance with high
                        potential for misuse and abuse, which can lead to the
                        development of substance use disorder, including
                        addiction.
                    </p>
                    <p>
                        Misuse and abuse of HYSINGLA ER increases risk of
                        overdose, which may lead to central nervous system and
                        respiratory depression, hypotension, seizures, and
                        death. The risk is increased with concurrent abuse of
                        HYSINGLA ER with alcohol and other CNS depressants.
                    </p>
                    <p>
                        Abuse may occur by taking intact tablets in quantities
                        greater than prescribed or without legitimate purpose,
                        by crushing and chewing or snorting the crushed
                        formulation, or by injecting a solution made from the
                        crushed formulation. Taking cut, broken, chewed,
                        crushed, or dissolved HYSINGLA ER increases the risk of
                        overdose and death.
                    </p>
                    <p>HYSINGLA ER is approved for oral use only.</p>
                </div>

                <div className="isi-content">
                    <p className="isi-h1">INDICATIONS AND USAGE</p>
                    <p>
                        HYSINGLA ER is indicated for the management of severe
                        and persistent pain that requires an extended treatment
                        period with a daily opioid analgesic and for which
                        alternative treatment options are inadequate.
                    </p>
                    <p className="text-underline">Limitations of Use</p>
                    <ul className="bullet-list">
                        <li>
                            Because of the risks of addiction, abuse, and misuse
                            with opioids, which can occur at any dosage or
                            duration, and because of the greater risks of
                            overdose and death with extended-release/long-acting
                            opioid formulations, reserve HYSINGLA ER for use in
                            patients for whom alternative treatment options
                            (e.g., non-opioid analgesics or immediate-release
                            opioids) are ineffective, not tolerated, or would be
                            otherwise inadequate to provide sufficient
                            management of pain.
                        </li>
                        <li>
                            HYSINGLA ER is not indicated as an as-needed (prn)
                            analgesic.
                        </li>
                    </ul>
                </div>
                <div className="isi-content last">
                    <p className="strong">
                        Please read{" "}
                        <ExternalLink
                            externalURL="https://dailymed.nlm.nih.gov/dailymed/fda/fdaDrugXsl.cfm?setid=b7d23ac2-e776-9f62-3290-c64c2d6eb353&type=display"
                            messageTitle={dailymedLinkInfo.TITLE}
                            messageBody={externalLinkInfo.BODY}
                            classNameLink={externalLinkInfo.LINK_CLASS_NAME}
                            classNameOverlay={
                                externalLinkInfo.OVERLAY_CLASS_NAME
                            }
                        >
                            Full Prescribing Information
                        </ExternalLink>
                        , including Boxed Warning.
                    </p>
                    <p>
                        To report SUSPECTED ADVERSE REACTIONS, contact Purdue
                        Pharma L.P. at{" "}
                        <nobr>
                            <a href="tel:+18887267535">1-888-726-7535</a>,
                        </nobr>{" "}
                        option 2, or FDA at{" "}
                        <nobr>
                            <a href="tel:+18003321088">1-800-FDA-1088</a>
                        </nobr>{" "}
                        or{" "}
                        <span>
                            <ExternalLink
                                externalURL="https://www.fda.gov/safety/medwatch"
                                messageTitle={externalLinkInfo.TITLE}
                                messageBody={externalLinkInfo.BODY}
                                classNameLink={externalLinkInfo.LINK_CLASS_NAME}
                                classNameOverlay={
                                    externalLinkInfo.OVERLAY_CLASS_NAME
                                }
                            >
                                www.fda.gov/safety/medwatch
                            </ExternalLink>
                        </span>
                        .
                    </p>
                    <p className="text-italics">
                        Intended for healthcare professionals of the United
                        States of America only.
                    </p>
                </div>
            </div>
        </section>
    );
}
